/**
 * Team Parsing Utilities
 * Parse team matchups from leg data
 */

/**
 * Minimal leg interface for team parsing
 */
export interface LegWithTeams {
  team?: string | null;
  opponentTeam?: string | null;
  /** True when `team` plays at home; null when the side is unknown. */
  isHomeGame?: boolean | null;
}

/**
 * Parse team names from leg data
 * Returns [awayTeam, homeTeam] or null if can't parse
 *
 * Handles different prop formats:
 * - Totals: "Team A vs Team B", or the web builder's "Away @ Home", in team field
 * - Spreads/Moneyline: team and opponentTeam fields
 * - Player props: team field is player's team
 */
export function parseTeams(leg: LegWithTeams): [string, string] | null {
  const team = leg.team || '';
  const opponent = leg.opponentTeam || '';

  const matchupSeparator = / vs | @ /i;
  if (matchupSeparator.test(team)) {
    const [away, home] = team.split(matchupSeparator).map((t) => t.trim());
    if (away && home) {
      return [away, home];
    }
  }

  // `team` is the bet team (or the player's team), so its side comes from isHomeGame.
  // Without the flag the side is unknown, so no order is claimed.
  if (team && opponent) {
    if (leg.isHomeGame === true) return [opponent, team];
    if (leg.isHomeGame === false) return [team, opponent];
    return null;
  }

  // If we only have team, try to infer from player's team
  if (team) {
    return [team, 'Opponent'];
  }

  return null;
}

/** A leg's real away and home teams, or null when either is unknown (no "Opponent" stand-in). */
export function getLegAwayHome(leg: LegWithTeams): { away: string; home: string } | null {
  const teams = parseTeams(leg);
  if (!teams || teams[1] === 'Opponent') return null;
  return { away: teams[0], home: teams[1] };
}

/** "AWAY @ HOME" when the side is known, "A vs B" when only the two teams are, else null. */
export function formatLegMatchup(
  leg: LegWithTeams,
  formatTeam: (team: string) => string = (team) => team,
): string | null {
  const matchup = getLegAwayHome(leg);
  if (matchup) return `${formatTeam(matchup.away)} @ ${formatTeam(matchup.home)}`;
  if (leg.team && leg.opponentTeam)
    return `${formatTeam(leg.team)} vs ${formatTeam(leg.opponentTeam)}`;
  return null;
}
